import { CustomRoute } from "@/interfaces";
import {
  AppWindowMac,
  Building2,
  // CheckSquare,
  DollarSign,
  FileText,
  FolderKanban,
  Gavel,
  LayoutDashboard,
  // Receipt,
  ShoppingCart,
  TrendingUp,
  User,
  Users,
  ClipboardCheck,
} from "lucide-react";
import React from "react";
import { Navigate } from "react-router";
import { usePermissions } from "@/lib/permissionUtils";
import { leadGenRoutes } from "./leadGen";
import VendorManagement from "@/modules/Admin/VendorManagement";
import CrmFullOnly from "@/modules/Admin/CRM/CrmFullOnly";

const EmployeeManagement = React.lazy(() => import("../modules/Admin/EmployeeManagement/index"));

const Dashboard = React.lazy(() => import("../modules/Employee/Dashboard"));
const AssociatedClients = React.lazy(() => import("../modules/Employee/AssociatedClients/index"));
const AdminClientManagement = React.lazy(() => import("../modules/Admin/ClientManagement/index"));
const Projects = React.lazy(() => import("../modules/Employee/Projects/index"));
const MyCommissions = React.lazy(() => import("../modules/Employee/MyCommissions"));
const Commission = React.lazy(() => import("../modules/Employee/Commission"));
const CommissionDisputes = React.lazy(() => import("../modules/Employee/CommissionDisputes"));
const CommissionApprovals = React.lazy(() => import("../modules/Admin/CommissionManagement/CommissionApprovals"));
const ProjectsApprovals = React.lazy(() => import("../modules/Admin/ProjectsApprovals"));
const OrderApprovalPage = React.lazy(() => import("@/modules/Admin/OMS/OrderApprovals"));

// const MyInvoices = React.lazy(() => import("../modules/Employee/Invoices/MyInvoices"));
const AllInvoices = React.lazy(() => import("../modules/Employee/Invoices/AllInvoices"));
const CreateInvoicePage = React.lazy(() => import("../modules/Employee/Invoices/CreateInvoicePage"));
// const VendorBills = React.lazy(() => import("../modules/Finance/VendorBillApprovals/AllBills"));
// const VendorBills = React.lazy(() => import("../modules/Admin/ListVendorBills/AllBills"));
// const VendorBillApprovals = React.lazy(() => import("../modules/Finance/VendorBillApprovals"));
// const CreateBillPage = React.lazy(() => import("../modules/Employee/Bills/CreateBillPage"));
const NextRecurringInvoices = React.lazy(() => import("../modules/Finance/NextRecurringInvoices"));

const EditProfile = React.lazy(() => import("../modules/Profile/EditProfile"));
const AllNotifications = React.lazy(() => import("../modules/Notifications/AllNotifications"));

const OrdersPage = React.lazy(() => import("@/modules/Admin/OMS/Orders/index.tsx"));
const DailyReportsPage = React.lazy(() => import("@/modules/Admin/OMS/DailyReports/DailyReportsPage.tsx"));
const CreateOrderPage = React.lazy(() => import("@/modules/Admin/OMS/Orders/CreateOrderPage.tsx"));
const DealsPage = React.lazy(() => import("@/modules/Admin/CRM/Deals/index.tsx"));
const DealDetailsPage = React.lazy(() => import("@/modules/Admin/CRM/Deals/DealDetailsPage.tsx"));
const DealPurchaseOrderPage = React.lazy(() => import("@/modules/Admin/CRM/Deals/DealPurchaseOrderPage.tsx"));
const CrmCompaniesPage = React.lazy(() => import("@/modules/Admin/CRM/Companies/index.tsx"));
const OldCrmCompaniesRedirect = React.lazy(() => import("@/modules/Admin/CRM/Companies/OldCrmCompaniesRedirect.tsx"));
const CrmCompanyDetailsPage = React.lazy(() => import("@/modules/Admin/CRM/Companies/CompanyDetailsPage.tsx"));
const CrmMeetingsPage = React.lazy(() => import("@/modules/Admin/CRM/Meetings/index.tsx"));
const PowerBiPage = React.lazy(() => import("@/modules/SuperAdmin/PowerBi"));
const PowerBiViewer = React.lazy(() => import("@/modules/SuperAdmin/PowerBi/PowerBiViewer"));
// const ProjectsPage = React.lazy(() => import("@/modules/Admin/OMS/Projects/index.tsx"));
// const ClosedWonLeadsPage = React.lazy(() => import("@/modules/Admin/OMS/ClosedWonLeads/index.tsx"));

const ClientManagementWrapper = () => {
  const { canEdit } = usePermissions();
  if (canEdit("clientManagement")) {
    return <AdminClientManagement />;
  }
  return <AssociatedClients />;
};


const employeeRoutes: CustomRoute[] = [
  {
    path: "/",
    id: "Employee",
    element: <Dashboard />,
    menuName: "Dashboard",
    icon: LayoutDashboard,
    highlightOn: ["dashboard"],
  },
  // Grouped like the Super Admin menu; every page keeps its own path and permission rules (UserMenu.tsx).
  {
    path: "",
    id: "Employee",
    element: null,
    menuName: "Projects",
    icon: FolderKanban,
    highlightOn: ["projects-approvals", "projects"],
    submenu: [
      // Shown only to CRM deal approvers (UserMenu.tsx); the server scopes it to their steps.
      {
        path: "/projects-approvals",
        id: "Employee",
        element: <ProjectsApprovals />,
        menuName: "Project Tasks",
        highlightOn: ["projects-approvals"],
      },
      {
        path: "/projects",
        id: "Employee",
        element: <Projects />,
        menuName: "My Projects",
        highlightOn: ["projects"],
        permissionKey: "projectManagement",
      },
    ],
  },
  {
    path: "",
    id: "Employee",
    element: null,
    menuName: "Directory",
    icon: Users,
    highlightOn: ["associated-clients", "VendorManagement", "organization", "employees", "employee"],
    submenu: [
      {
        path: "/associated-clients",
        id: "Employee",
        element: <ClientManagementWrapper />,
        menuName: "Associated Clients",
        highlightOn: ["associated-clients"],
        permissionKey: "clientManagement",
      },
      {
        path: "/vendor-management",
        id: "Employee",
        element: <VendorManagement />,
        menuName: "Vendor Management",
        highlightOn: ["VendorManagement", "organization"],
        permissionKey: "vendorManagement",
      },
      {
        path: "/employee-management",
        id: "Employee",
        element: <EmployeeManagement />,
        menuName: "Employee Management",
        highlightOn: ["employees", "employee"],
        permissionKey: "employeeManagement",
      },
    ],
  },
  // Commission approvers (Super Admin → Commission Approvers) see this as a top-level menu only
  // when the Commissions menu is hidden for them; otherwise it shows as the Commissions submenu
  // entry below (UserMenu.tsx). This entry is the one that registers the route.
  {
    path: "/my-commission-approvals",
    id: "Employee",
    element: <CommissionApprovals />,
    menuName: "Commission Approvals",
    icon: ClipboardCheck,
    highlightOn: ["my-commission-approvals"],
  },
  {
    path: "",
    id: "Employee",
    element: null,
    menuName: "Commissions",
    icon: DollarSign,
    highlightOn: ["my-commissions", "commission"],
    permissionKey: "commissionManagement",
    submenu: [
      {
        path: "/my-commissions",
        id: "Employee",
        element: <MyCommissions />,
        menuName: "My Commissions",
        icon: DollarSign,
        highlightOn: ["my-commissions"],
        permissionKey: "commissionManagement",
      },
      {
        path: "/commission",
        id: "Employee",
        element: <Commission />,
        menuName: "Track Commissions",
        icon: TrendingUp,
        highlightOn: ["commission"],
        permissionKey: "commissionManagement",
      },
      {
        path: "/commission-disputes",
        id: "Employee",
        element: <CommissionDisputes />,
        menuName: "Commission Disputes",
        icon: Gavel,
        highlightOn: ["commission-disputes"],
        permissionKey: "commissionManagement",
      },
      {
        // Menu link only -- the route itself is registered by the top-level entry.
        path: "/my-commission-approvals",
        id: "Employee",
        element: null,
        menuName: "Commission Approvals",
        icon: ClipboardCheck,
        highlightOn: ["my-commission-approvals"],
      },
    ],
  },
  {
    path: "",
    id: "Employee",
    element: null,
    menuName: "Financials",
    icon: AppWindowMac,
    highlightOn: ["invoices"],
    submenu: [
      // {
      //   path: "/my-invoices",
      //   id: "Employee",
      //   element: <MyInvoices />,
      //   menuName: "My Invoices",
      //   icon: FileText,
      //   highlightOn: ["my-invoices"],
      //   permissionKey: "BillingInvoices",
      // },
      {
        path: "/all-invoices",
        id: "Employee",
        element: <AllInvoices />,
        menuName: "All Invoices",
        icon: FileText,
        highlightOn: ["all-invoices"],
        permissionKey: "BillingInvoices",
      },
      {
        path: "/my-invoices/create",
        id: "Employee",
        element: <CreateInvoicePage />,
        menuName: "Create Invoice",
        icon: FileText,
        highlightOn: ["my-invoices"],
        permissionKey: "BillingInvoices",
      },
      //       {
      //         path: "/billing/recurring-invoices",
      //         id: "Employee",
      //         element: <RecurringInvoices />,
      //         menuName: "Recurring Transactions",
      //         icon: Repeat,
      //         highlightOn: ["recurring-invoices"],
      //         permissionKey: "BillingInvoices",
      //         hideInMenu: !isQuickBooks,
      //       },
      {
        path: "/billing/next-recurring-invoices",
        id: "Admin",
        element: <NextRecurringInvoices />,
        menuName: "Upcoming Invoices",
        icon: Building2,
        highlightOn: ["next-recurring-invoices"],
        permissionKey: "BillingInvoices",
      },
      // {
      //   path: "/vendor-bills",
      //   id: "Employee",
      //   element: <VendorBills />,
      //   menuName: "Vendor Bills",
      //   icon: Receipt,
      //   highlightOn: ["vendor-bills"],
      //   permissionKey: "BillingInvoices",
      // },
      // {
      //   path: "/vendor-bill-approvals",
      //   id: "Employee",
      //   element: <VendorBillApprovals />,
      //   menuName: "Vendor Bill Approvals",
      //   icon: CheckSquare,
      //   highlightOn: ["vendor-bill-approvals"],
      //   permissionKey: "BillingInvoices",
      // },
      // {
      //   path: "/vendor-bills/create",
      //   id: "Employee",
      //   element: <CreateBillPage />,
      //   menuName: "Create Bill",
      //   icon: FileText,
      //   highlightOn: ["create-bill"],
      //   permissionKey: "BillingInvoices",
      // },
    ],
  },
  {
    path: "",
    id: "Employee",
    element: null,
    menuName: "OMS",
    icon: ShoppingCart,
    highlightOn: ["oms", "orders", "approvals"],
    // permissionKey: "oms",
    submenu: [
      // {
      //   path: "/oms/closed-won",
      //   id: "Employee",
      //   element: <ClosedWonLeadsPage />,
      //   menuName: "Closed Won Leads",
      //   highlightOn: ["closed-won"],
      // },
      {
        path: "/oms/orders",
        id: "Employee",
        element: <OrdersPage />,
        menuName: "Orders",
        highlightOn: ["orders"],
      },
      {
        path: "/oms/orders/create",
        id: "Employee",
        element: <CreateOrderPage />,
        hideInMenu: true,
        menuName: "Orders",
        highlightOn: ["orders"],
      },
      {
        path: "/oms/orders/:id/edit",
        id: "edit-order-route",
        element: <CreateOrderPage />,
        menuName: "Edit Order",
        highlightOn: ["orders", "edit-order"],
        hideInMenu: true,
      },
      // Orders that aren't part of a CRM project (L1 Ops → L4 Margin); a CRM project's order is
      // approved on its Create Order step in Project Tasks
      {
        path: "/oms/approvals",
        id: "Employee",
        element: <OrderApprovalPage />,
        menuName: "Order Approvals",
        highlightOn: ["approvals"],
      },
      {
        path: "/oms/consumption-report",
        id: "Employee",
        element: <DailyReportsPage />,
        menuName: "Consumption Report",
        highlightOn: ["daily-reports"],
        permissionKey: "oms_operations",
      },
      // {
      //   path: "/oms/projects",
      //   id: "Employee",
      //   element: <ProjectsPage />,
      //   menuName: "Projects",
      //   highlightOn: ["projects"],
      // },
    ],
  },
  // One CRM section: Lead Gen's lead pipeline (by Lead Gen role), then the CRM module, whose visibility is decided by
  // getCrmAccess (OMS + OMS Sales/Ops), not a permissionKey; see UserMenu.
  leadGenRoutes("Employee", {
    crm: [
      // CRM Companies moved from /crm/companies (it read like Super Admin's /companies): old links redirect
      ...["/crm/companies", "/crm/companies/:id"].map((path) => ({
        path,
        id: "Employee",
        element: <OldCrmCompaniesRedirect />,
        hideInMenu: true,
        menuName: "CRM Companies",
        crmFullOnly: true,
      })),
      // Contacts are a tab of CRM Companies; old links land on it
      {
        path: "/crm/contacts",
        id: "Employee",
        element: <Navigate to="/crm/crm-companies?tab=contacts" replace />,
        hideInMenu: true,
        menuName: "CRM Companies",
                crmFullOnly: true,
      },
      {
        path: "/crm/crm-companies",
        id: "Employee",
        element: (
          <CrmFullOnly>
            <CrmCompaniesPage />
          </CrmFullOnly>
        ),
        menuName: "CRM Companies",
                crmFullOnly: true,
      },
      {
        path: "/crm/crm-companies/:id",
        id: "Employee",
        element: (
          <CrmFullOnly>
            <CrmCompanyDetailsPage />
          </CrmFullOnly>
        ),
        hideInMenu: true,
        menuName: "CRM Companies",
                crmFullOnly: true,
      },
      {
        path: "/crm/deals",
        id: "Employee",
        element: <DealsPage />,
        menuName: "Deals",
        highlightOn: ["deals"],
      },
      {
        path: "/crm/deals/:id",
        id: "Employee",
        element: <DealDetailsPage />,
        hideInMenu: true,
        menuName: "Deals",
        highlightOn: ["deals"],
      },
      // Generate PO: a PO kept with the deal (new, or an existing one)
      ...["/crm/deals/:id/po", "/crm/deals/:id/po/:poId"].map((path) => ({
        path,
        id: "Employee",
        element: <DealPurchaseOrderPage />,
        hideInMenu: true,
        menuName: "Deals",
        highlightOn: ["deals"],
      })),
      {
        path: "/crm/meetings",
        id: "Employee",
        element: (
          <CrmFullOnly>
            <CrmMeetingsPage />
          </CrmFullOnly>
        ),
        menuName: "Meetings",
        highlightOn: ["meetings"],
        crmFullOnly: true,
      },
    ],
  }),
  {
    path: "/powerbi",
    id: "Employee",
    element: <PowerBiPage />,
    menuName: "Power BI",
    icon: FileText,
    highlightOn: ["powerbi"],
  },
  {
    path: "/powerbi/view/:id",
    id: "Employee",
    element: <PowerBiViewer />,
    menuName: "Power BI Viewer",
    hideInMenu: true,
    highlightOn: ["powerbi"],
  },
  {
    path: "",
    id: "Employee",
    element: null,
    menuName: "Account",
    icon: User,
    highlightOn: ["notifications", "profile"],
    submenu: [
      {
        path: "/notifications",
        id: "Employee",
        element: <AllNotifications />,
        menuName: "Notifications",
        highlightOn: ["notifications"],
      },
      {
        path: "/profile",
        id: "Employee",
        element: <EditProfile />,
        menuName: "Profile",
        highlightOn: ["profile"],
      },
    ],
  },
];

export default employeeRoutes;
